import { Text } from "@gravity-ui/uikit";
import { AnimatePresence, motion } from "framer-motion";

import { useRateCardTranslate } from "../hooks/useRateCardTranslate";
import css from "./RateCard.module.scss";

const SEPARATOR = "·";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface CountdownSegmentProps {
  value: number;
  unit: string;
}

const CountdownSegment = ({ value, unit }: CountdownSegmentProps) => (
  <span className={css.countdownSegmentWrapper}>
    <AnimatePresence initial={false} mode="popLayout">
      <motion.span
        key={value}
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 10, opacity: 0 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className={css.countdownValue}
      >
        {String(value).padStart(2, "0")}
      </motion.span>
    </AnimatePresence>
    <span className={css.countdownUnit}>{unit}</span>
  </span>
);

interface DiscountCountdownProps {
  timeLeft: number;
}

export const DiscountCountdown = ({ timeLeft }: DiscountCountdownProps) => {
  const i18n = useRateCardTranslate();

  const totalSeconds = Math.floor(timeLeft / 1000);
  const d = Math.floor(totalSeconds / (60 * 60 * 24));
  const h = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const m = Math.floor((totalSeconds % (60 * 60)) / 60);
  const s = totalSeconds % 60;

  const isUrgent = timeLeft < ONE_DAY_MS;

  return (
    <motion.div
      className={css.discountTimerBlock}
      animate={
        isUrgent
          ? { opacity: [1, 0.55, 1], scale: [1, 1.015, 1] }
          : { opacity: 1, scale: 1 }
      }
      transition={
        isUrgent
          ? { repeat: Infinity, duration: 1.4, ease: "easeInOut" }
          : { duration: 0 }
      }
    >
      <Text
        variant="caption-1"
        color="secondary"
        className={css.countdownLabel}
      >
        {i18n.countdownLabel}
      </Text>
      <div className={css.countdownSegments}>
        <CountdownSegment value={d} unit={i18n.days} />
        <span className={css.countdownSeparator}>{SEPARATOR}</span>
        <CountdownSegment value={h} unit={i18n.hours} />
        <span className={css.countdownSeparator}>{SEPARATOR}</span>
        <CountdownSegment value={m} unit={i18n.minutes} />
        <span className={css.countdownSeparator}>{SEPARATOR}</span>
        <CountdownSegment value={s} unit={i18n.seconds} />
      </div>
    </motion.div>
  );
};
